<script setup lang="ts">
    import {
        useGetCoachingForm,
        useGetCoachingForms,
    } from '@/js/services/api/coachingForm/coachingFormQueries';
    import { __, __untypedExpression } from '@/js/stores/expressions';
    import { computed, ref, watch } from 'vue';
    import { type Option } from '@/js/components/autoComplete/types';
    import TextInput from '../../TextInput.vue';
    import { CoachingType } from '@/js/services/api/protocol/protocolModel';
    import { useCoachInteractionStore } from '@/js/stores/coach-interaction';
    import RadioOption from '../../RadioOption.vue';
    import CoachingFormController from '../../coaching/form/CoachingFormController.vue';
    import LoadingDots from '../../LoadingDots.vue';
    import EchoButton from '../../EchoButton.vue';
    import {
        useMutationPostProtocolWithoutInteraction,
        useQueryGetCoachableAgents,
    } from '@/js/services/api/protocol/protocolQueries';
    import { generatePostProtocolWithoutInteractionRequest } from '../../coaching/form/coachingForm.utils';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import AutoComplete from '../../autoComplete/AutoComplete.vue';
    import { useGetMyAccount } from '@/js/hooks/user';
    import dayjs from 'dayjs';
    import { useGetChannels } from '@/js/services/api/channels/channelQueries';
    import { useToastStore } from '@/js/stores/snackbar';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { PermissionArea, QualityAssuranceModulePermissionFlag } from '@/js/consts/permissions';
    import { CheckIcon, PlusIcon } from '@lucide/vue';

    const { hasPermission } = usePermissions();

    const canCoach = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.ManualCoaching
    );

    const { data: forms, isLoading } = useGetCoachingForms();

    const { data: channels, isFetched } = useGetChannels();

    const toast = useToastStore();

    const channelOptions = computed(() => {
        return channels.value?.channels ?? [];
    });

    const channelNameToKey = (name?: string) => {
        if (!name) return 'channel-name-select';

        const normalized = name.trim().toLowerCase();

        switch (normalized) {
            case 'call':
                return 'channel-name-call';
            case 'chat':
                return 'channel-name-chat';
            case 'mail':
            case 'email':
                return 'channel-name-mail';
            case 'callback':
                return 'channel-name-callback';
            case 'outgoing call':
            case 'outgoing-call':
            case 'outgoing_call':
                return 'channel-name-outgoing-call';
            default:
                return ''; // fallback handled below
        }
    };

    const channelAutoCompleteOptions = computed(() =>
        channelOptions.value.map((c) => ({
            value: c.id,
            name: channelNameToKey(c.name) ? __untypedExpression(channelNameToKey(c.name)) : c.name,
        }))
    );

    const selectedChannelSearch = ref<string | Option | null>(null);

    const onChannelSelect = (option: Option) => {
        coachInteractionStore.protocol.channelId = option.value;
    };

    const onChannelClear = () => {
        coachInteractionStore.protocol.channelId = undefined;
        selectedChannelSearch.value = null;
    };

    const selectedFormId = ref<string>('');

    const {
        data: selectedForm,
        isFetching: isLoadingSelectedForm,
        refetch: refetchSelectedForm,
    } = useGetCoachingForm(selectedFormId);

    const { data: userData } = useGetMyAccount();

    const successSavedByMessage = ref<string>('');

    const { data: agentData, isLoading: isLoadingAgents } = useQueryGetCoachableAgents();

    const manualForms = computed(() => {
        if (!forms.value) return [];

        return forms.value.forms
            .filter((form) => form.isActive && !form.useAi)
            .sort((a, b) => a.title.localeCompare(b.title));
    });

    const formOptions = computed(() =>
        manualForms.value.map((f) => ({ value: f.id, name: f.title }))
    );

    const selectedFormSearch = ref<string | Option | null>(null);

    const onFormSelect = (option: Option) => {
        selectedFormId.value = option.value;
    };

    const onFormClear = () => {
        selectedFormId.value = '';
        selectedFormSearch.value = null;
    };

    watch(manualForms, (newVal) => {
        if (newVal.length > 0 && !isLoading.value) {
            const first = newVal[0];
            selectedFormId.value = first.id;
            selectedFormSearch.value = { value: first.id, name: first.title };
        }
    });

    watch(selectedFormId, (newVal) => {
        if (newVal) {
            refetchSelectedForm();
        } else if (!selectedForm.value && manualForms.value.length > 0) {
            selectedFormId.value = manualForms.value[0].id;
        }
    });

    const agentOptions = computed(() => {
        return (
            agentData?.value?.agents.map((agent) => ({
                value: agent.id,
                name: agent.name,
            })) || []
        );
    });
    const selectedAgentSearch = ref<string | null>(null);
    const selectedAgentId = ref<string>('');

    const onAgentSelect = (option: { value: string; name: string }) => {
        selectedAgentId.value = option.value;
    };

    const onAgentClear = () => {
        selectedAgentId.value = '';
        selectedAgentSearch.value = null;
    };

    const { mutateAsync: createProtocol, isPending: submitLoading } =
        useMutationPostProtocolWithoutInteraction();

    const coachInteractionStore = useCoachInteractionStore();

    const successDialogStore = useSuccessDialog();

    const coachingComplete = ref(false);

    const onSubmit = async () => {
        if (!selectedAgentId.value || !coachInteractionStore.protocol.channelId) return;

        try {
            const req = generatePostProtocolWithoutInteractionRequest(
                coachInteractionStore.protocol,
                selectedAgentId.value,
                coachInteractionStore.manualInteractionReference,
                selectedFormId.value
            );

            const res = await createProtocol(req);

            if (res) {
                successSavedByMessage.value = __('manual-coaching-saved-by', {
                    name: userData.value?.name || '',
                    date: dayjs().format('YYYY-MM-DD HH:mm'),
                });

                successDialogStore.openDialog({
                    message: __('manual-coaching-success'),
                });

                coachingComplete.value = true;
            }
        } catch (error: any) {
            if (error?.response?.status === 400) {
                toast.addToast('error', __('error-response'));
                return;
            }
        }
    };

    const resetForm = () => {
        if (selectedForm.value) {
            coachInteractionStore.initProtocol(selectedForm.value);
            selectedAgentId.value = '';
            selectedAgentSearch.value = null;
            coachInteractionStore.manualInteractionReference = undefined;
            successSavedByMessage.value = '';
            coachingComplete.value = false;
            coachInteractionStore.protocol.channelId = undefined;
            selectedChannelSearch.value = null;
        }
    };

    const saveButtonDisabled = computed(() => {
        return (
            !canCoach.value ||
            !selectedAgentId.value ||
            !coachInteractionStore.isValid ||
            coachingComplete.value ||
            !coachInteractionStore.protocol.channelId
        );
    });
</script>
<template>
    <div class="flex justify-between items-start">
        <h2 class="heading-md pb-8">
            {{ __('manual-coaching-title') }}
        </h2>
        <EchoButton :disabled="!selectedForm" :variant="'primary'" @click="resetForm">
            {{ __('new-coaching') }}
            <template #icon-right><PlusIcon /></template>
        </EchoButton>
    </div>
    <div class="flex flex-col w-100 xl:w-full xl:flex-row gap-5 xl:items-center">
        <div>
            <h4 class="text-body-bold uppercase pb-1">
                {{ __('quality-assurance-coach-interaction-choose-form') }}
            </h4>

            <AutoComplete
                v-model="selectedFormSearch"
                input-id="coaching-form-autocomplete"
                :aria-label="__('schedule-coaching-form-select-form-title')"
                :options="formOptions"
                :disabled="coachingComplete"
                class="w-full xl:w-50"
                @select="onFormSelect"
                @clear="onFormClear"
            />
        </div>
        <div>
            <h4 class="text-body-bold uppercase pb-1">
                {{ __('agent') }}
            </h4>
            <AutoComplete
                v-model="selectedAgentSearch"
                input-id="coaching-agent-autocomplete"
                :aria-label="__('agent')"
                :options="agentOptions"
                :disabled="isLoadingAgents || coachingComplete"
                class="w-full xl:max-w-50"
                @select="onAgentSelect"
                @clear="onAgentClear"
            />
        </div>
        <div>
            <h4 class="text-body-bold uppercase pb-1">
                {{ __('interaction-id') }}
            </h4>
            <TextInput
                id="interaction-id-input"
                v-model="coachInteractionStore.manualInteractionReference"
                :aria-label="__('interaction-id')"
                class="w-full xl:max-w-50"
                :disabled="coachingComplete"
            />
        </div>
        <div>
            <h4 class="text-body-bold uppercase pb-1">
                {{ __('interaction-media-type') }}
            </h4>
            <AutoComplete
                v-model="selectedChannelSearch"
                input-id="coaching-channel-autocomplete"
                :aria-label="__('interaction-media-type')"
                :options="channelAutoCompleteOptions"
                :disabled="coachingComplete || !isFetched"
                :placeholder="__('channel-name-select')"
                class="w-full xl:w-50"
                @select="onChannelSelect"
                @clear="onChannelClear"
            />
        </div>
        <div class="flex items-baseline -ml-3">
            <div class="flex items-center gap-7 h-10.5 px-7">
                <RadioOption
                    id="sideBySide"
                    v-model="coachInteractionStore.protocol.coachingType"
                    name="coachingType"
                    :label="__('side-by-side')"
                    :value="CoachingType.SIDE_BY_SIDE"
                    :disabled="coachingComplete"
                />
                <RadioOption
                    id="remote"
                    v-model="coachInteractionStore.protocol.coachingType"
                    name="coachingType"
                    :label="__('remote')"
                    :value="CoachingType.REMOTE"
                    :disabled="coachingComplete"
                />
            </div>
        </div>
    </div>
    <div class="py-12 w-full flex justify-center">
        <div v-if="selectedFormId && !isLoadingSelectedForm && selectedForm" class="w-full">
            <CoachingFormController :form="selectedForm" :preview="coachingComplete" />
            <div class="page-content-width flex justify-between items-center pt-8">
                <p class="text-caption">
                    {{ successSavedByMessage }}
                </p>
                <div class="flex gap-6">
                    <EchoButton variant="tertiary" :disabled="!selectedForm" @click="resetForm">
                        {{ __('cancel') }}
                    </EchoButton>
                    <EchoButton
                        variant="primary"
                        :disabled="saveButtonDisabled"
                        :loading="submitLoading"
                        :aria-label="__('save')"
                        @click="onSubmit"
                    >
                        <template v-if="!coachingComplete">
                            {{ __('save') }}
                        </template>
                        <template v-else>
                            {{ __('saved') }}
                        </template>
                        <template #icon-right><CheckIcon /></template>
                    </EchoButton>
                </div>
            </div>
        </div>
        <div v-else-if="isLoadingSelectedForm" class="h-32 flex items-center justify-center">
            <LoadingDots />
        </div>
    </div>
</template>
